import { ArrowPath, ArrowUpTray, PencilSquare, Plus } from '@medusajs/icons';
import { Button, Drawer, Input, Label, Skeleton, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ActionMenu } from '../../../components/action-menu';
import {
  SectionContainer,
  SectionEmpty,
  SectionHeader,
  SectionRow,
} from '../../../components/section';
import {
  createSetting,
  type ExchangeRatesRefresh,
  isFileSetting,
  refreshExchangeRates,
  SETTINGS_QUERY_KEY,
  type Setting,
  settingFileUrl,
  settingsQueryOptions,
  updateSetting,
  uploadSettingFile,
} from '../../../features/settings/settings.api';
import { ApiError } from '../../../lib/api';
import { formatDate, formatDateTime } from '../../../lib/format';

export const Route = createFileRoute('/_authenticated/setari/')({
  component: SetariPagina,
});

/**
 * Setările aplicației.
 *
 * **Listă de citit, nu formular de completat.** Varianta cu un `input` și un buton pe
 * fiecare rând arăta ca un formular cu douăzeci de câmpuri deschise deodată, deși
 * operatorul schimbă o valoare la câteva luni. Rândurile se citesc; editarea se cere
 * explicit și se face în sertar.
 */
function SetariPagina() {
  const { data, isPending, error } = useQuery(settingsQueryOptions());
  const [editing, setEditing] = useState<Setting | null>(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState<Setting | null>(null);

  return (
    <>
      <SectionContainer>
        <SectionHeader
          title="Setările aplicației"
          actions={
            <div className="flex gap-x-2">
              <RefreshExchangeRatesButton />
              <Button size="small" variant="secondary" onClick={() => setCreating(true)}>
                <Plus />
                Adaugă
              </Button>
            </div>
          }
        />

        {isPending ? (
          <div className="flex flex-col gap-3 px-6 py-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : error ? (
          <SectionEmpty tone="error">
            {error instanceof Error ? error.message : 'Setările nu au putut fi citite.'}
          </SectionEmpty>
        ) : data.length === 0 ? (
          <SectionEmpty>Nicio setare definită.</SectionEmpty>
        ) : (
          data.map((setting) => (
            // Cheia e **numele**, nu id-ul: o setare declarată în cod, încă nescrisă, n-are
            // id. Numele e oricum identificatorul stabil al setării.
            <SectionRow
              key={setting.name}
              title={<span className="font-mono">{setting.name}</span>}
              value={
                isFileSetting(setting) ? (
                  <SettingFilePreview setting={setting} />
                ) : setting.dated ? (
                  <DatedSettingValue setting={setting} />
                ) : (
                  setting.value
                )
              }
              actions={
                <ActionMenu
                  groups={[
                    {
                      actions: [
                        isFileSetting(setting)
                          ? {
                              icon: <ArrowUpTray />,
                              label: 'Încarcă',
                              onClick: () => setUploading(setting),
                            }
                          : {
                              icon: <PencilSquare />,
                              label: 'Modifică',
                              onClick: () => setEditing(setting),
                            },
                      ],
                    },
                  ]}
                />
              }
            />
          ))
        )}
      </SectionContainer>

      <SettingDrawer
        setting={editing}
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />

      <SettingFileDrawer setting={uploading} onClose={() => setUploading(null)} />
    </>
  );
}

/**
 * „Ia cursul de la BNR" (fondatorul, 2026-10-14, #214): aceeași rulare pe care o face workerul
 * luni–vineri la 13:05, pornită acum.
 *
 * Scrie doar **cursul de azi**: dimineața, BNR n-a publicat încă, fișierul e de ieri, iar butonul
 * spune asta fără să scrie nimic. Cursul de mână rămâne posibil, din „Modifică".
 */
function RefreshExchangeRatesButton() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: refreshExchangeRates,
    onSuccess: async (result) => {
      const message = refreshMessage(result);

      if (result.kind === 'written') {
        toast.success(message);
      } else {
        toast.warning(message);
      }

      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Cursul nu s-a putut cere de la BNR.',
      );
    },
  });

  return (
    <Button
      size="small"
      variant="secondary"
      onClick={() => mutation.mutate()}
      isLoading={mutation.isPending}
    >
      <ArrowPath />
      Ia cursul de la BNR
    </Button>
  );
}

function refreshMessage(result: ExchangeRatesRefresh): string {
  switch (result.kind) {
    case 'written':
      return `Cursul BNR din ${formatDate(result.rates.date)}: EUR ${result.rates.eur.replace('.', ',')}, USD ${result.rates.usd.replace('.', ',')}.`;
    case 'stale':
      return `BNR n-a publicat încă cursul de azi (ultimul e din ${formatDate(result.rates.date)}). Nu s-a schimbat nimic; încearcă după 13:00.`;
    case 'failed':
      return `Cursul nu s-a putut lua de la BNR: ${result.message}`;
  }
}

/**
 * Același sertar pentru adăugare și pentru modificare.
 *
 * La modificare, **numele e blocat**: pe el se leagă consumatorii din cod, iar o
 * redenumire ar lăsa codul căutând un nume care nu mai există. Cine chiar vrea altul
 * adaugă o setare nouă.
 */
function SettingDrawer({
  setting,
  open,
  onClose,
}: {
  setting: Setting | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');

  // Câmpurile se reîncarcă la fiecare deschidere: altfel al doilea rând editat ar porni
  // cu ce scrisese operatorul în primul.
  useEffect(() => {
    if (open) {
      setName(setting?.name ?? '');
      setValue(setting?.value ?? '');
    }
  }, [open, setting]);

  const mutation = useMutation({
    mutationFn: () =>
      setting ? updateSetting({ name: setting.name, value }) : createSetting({ name, value }),
    onSuccess: async () => {
      toast.success(setting === null ? 'Setare adăugată' : 'Setare actualizată');
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      // Mesajul serverului, nu unul inventat: „Setarea «app.timezone» există deja" spune
      // ce e de schimbat; „a eșuat" nu.
      toast.error(error instanceof ApiError ? error.message : 'Salvarea a eșuat.');
    },
  });

  const isDirty = setting ? value !== (setting.value ?? '') : name.trim().length > 0;

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{setting ? `Modifică ${setting.name}` : 'Adaugă o setare'}</Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-2">
            <Label htmlFor="setting-name">Nume</Label>
            <Input
              id="setting-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              // Numele existent nu se poate schimba; câmpul rămâne vizibil pentru context.
              disabled={setting !== null || mutation.isPending}
              maxLength={100}
              className="font-mono"
              placeholder="arie.cheie"
            />
            {setting === null ? (
              <Text size="small" className="text-ui-fg-subtle">
                Litere mici, cifre, punct, minus sau underscore. Numele nu se mai poate schimba după
                creare.
              </Text>
            ) : null}
          </div>

          <div className="flex flex-col gap-y-2">
            <Label htmlFor="setting-value">Valoare</Label>
            <Input
              id="setting-value"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              // Coloana e `VARCHAR(255)`. Limita e și pe server; aici oprește tastarea
              // înainte de o valoare care oricum ar fi respinsă.
              maxLength={255}
              // `text`, nu `number`: controlul numeric refuză tăcut virgula, iar rotița mouse-ului
              // schimbă cursul peste care trece cursorul.
              inputMode={setting?.type === 'number' ? 'decimal' : undefined}
              disabled={mutation.isPending}
            />
            {setting?.dated ? (
              <Text size="small" className="text-ui-fg-subtle">
                Cursul în lei pentru o unitate, cu punct sau virgulă: 4,9750. Îl scrie singur
                aplicația, de la BNR, în zilele lucrătoare la 13:05; o valoare scrisă aici rămâne
                până atunci.
                {setting.updatedAt
                  ? ` Salvat ultima oară: ${formatDateTime(setting.updatedAt)}.`
                  : ' Valoarea de acum n-are dată.'}
              </Text>
            ) : null}
          </div>
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            // Dezactivat cât timp nu s-a schimbat nimic: o salvare fără modificare nu
            // produce nici valoare nouă, nici rând de jurnal, iar operatorul ar crede că
            // n-a mers. „Reconfirmă pentru azi" a ieșit cu #214: vechimea nu mai blochează.
            disabled={!isDirty || mutation.isPending}
          >
            Salvează
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}

/**
 * Valoarea unei setări datate, cu ziua ei: cursul de azi și cel de acum o săptămână arată la fel
 * fără dată, iar diferența e chiar ce caută operatorul.
 */
function DatedSettingValue({ setting }: { setting: Setting }) {
  return (
    <span className="flex flex-col">
      <span>{setting.value ?? '-'}</span>
      <Text size="xsmall" className="text-ui-fg-muted">
        {setting.updatedAt
          ? `salvat ${formatDateTime(setting.updatedAt)}`
          : setting.value
            ? 'fără dată — salvează-l din nou'
            : 'necompletat'}
      </Text>
    </span>
  );
}

/**
 * Previzualizarea fișierului, direct în listă.
 *
 * Valoarea unei setări `file` e o cheie din storage — un rând de hexazecimale care nu
 * spune nimic operatorului. Ce contează e dacă semnătura încărcată e cea bună, iar asta se
 * vede doar uitându-te la ea.
 *
 * Fundalul în tablă de șah: un PNG de semnătură e transparent, iar pe alb ar fi
 * imposibil de spus dacă s-a încărcat o imagine goală.
 */
function SettingFilePreview({ setting }: { setting: Setting }) {
  if (setting.value === null) {
    return (
      <Text size="small" className="text-ui-fg-muted">
        Niciun fișier încărcat
      </Text>
    );
  }

  return (
    <img
      src={settingFileUrl(setting)}
      alt={setting.name}
      className="max-h-12 max-w-[160px] rounded border border-ui-border-base bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#ffffff_0%_50%)] bg-[length:12px_12px] object-contain p-1"
    />
  );
}

/**
 * Încărcarea fișierului unei setări `file`.
 *
 * ⚠️ **Nu există câmp de valoare aici, și asta e intenția.** Valoarea se schimbă doar prin
 * încărcare: o cheie de storage scrisă cu mâna ar arăta exact ca una salvată de o
 * încărcare reușită, până când consumatorul ar căuta un fișier care nu există. Serverul
 * respinge oricum un `PATCH` pe o setare `file` — ecranul nu e singura apărare.
 */
function SettingFileDrawer({ setting, onClose }: { setting: Setting | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  // Selecția se golește la fiecare deschidere: altfel a doua setare încărcată ar porni cu
  // fișierul ales pentru prima.
  useEffect(() => {
    if (setting) {
      setFile(null);
    }
  }, [setting]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!setting || !file) {
        throw new Error('Nu s-a ales niciun fișier.');
      }

      return uploadSettingFile({ name: setting.name, file });
    },
    onSuccess: async () => {
      toast.success('Fișier încărcat');
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Încărcarea a eșuat.');
    },
  });

  return (
    <Drawer open={setting !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{setting ? `Încarcă ${setting.name}` : 'Încarcă un fișier'}</Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4">
          {setting?.value ? (
            <div className="flex flex-col gap-y-2">
              <Label>Fișierul de acum</Label>
              <SettingFilePreview setting={setting} />
            </div>
          ) : null}

          <div className="flex flex-col gap-y-2">
            <Label htmlFor="setting-file">Fișier nou</Label>
            <Input
              id="setting-file"
              type="file"
              // `accept` e o comoditate — filtrează ce vede operatorul în dialogul de
              // fișiere. Formatul se verifică pe server, după **conținut**, nu după
              // extensie.
              accept="image/png"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              disabled={mutation.isPending}
            />
            <Text size="small" className="text-ui-fg-subtle">
              PNG, cel mult 1 MB. Fișierul înlocuiește ce e acum, fără să atingă documentele deja
              generate.
            </Text>
          </div>
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={file === null || mutation.isPending}
          >
            Încarcă
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}
